import { InspirationPublishedStatus } from "@/types/inspiration"
import { InitProfileProps, InitPublicProfileProps } from "@/types/profile"
import { AxiosInstance } from "axios"

import { CollectionsApi } from "@/lib/collections-api"
import { InspirationApi } from "@/lib/inspiration-api"
import { PagingMeta } from "@/lib/paging"
import { UserApi } from "@/lib/user-api"
import { compact } from "@/lib/utils/collections"

const emptyMeta: PagingMeta = {
  total_count: 0,
  current_page: 1,
  next_page: null,
  prev_page: null,
  total_pages: 0,
  per_page: 1,
}

export async function fetchPublicProfileProps(
  authorizedServerAxios: AxiosInstance,
  handle: string
): Promise<InitPublicProfileProps> {
  const userApi = new UserApi(authorizedServerAxios)
  const inspirationApi = new InspirationApi(authorizedServerAxios)
  const collectionsApi = new CollectionsApi(authorizedServerAxios)

  const [user, userProfile, recentGuidesResult, recentCollectionsResult] =
    await Promise.all([
      userApi.fetchPublicUser(handle),
      userApi.fetchPublicUserProfile(handle),
      inspirationApi
        .fetchPublicUserInspirations({
          handle,
          page: 1,
          perPage: 1,
        })
        .catch(() => ({
          data: [],
          meta: emptyMeta,
        })),
      collectionsApi
        .fetchPublicUserCollections({
          handle,
          page: 1,
          perPage: 1,
        })
        .catch(() => ({
          data: [],
          meta: emptyMeta,
        })),
    ])

  const recentGuideImage = recentGuidesResult.data[0]?.photos?.[0]?.url
  const recentCollectionImage = recentCollectionsResult.data[0]?.images?.[0]
  const guidesCount = recentGuidesResult.meta?.total_count ?? 0
  const collectionsCount = recentCollectionsResult.meta?.total_count ?? 0

  return compact({
    user,
    userProfile,
    recentGuideImage,
    recentCollectionImage,
    guidesCount,
    collectionsCount,
  })
}

export async function fetchProfileProps(
  authorizedServerAxios: AxiosInstance
): Promise<Omit<InitProfileProps, "initUser">> {
  const userApi = new UserApi(authorizedServerAxios)
  const inspirationApi = new InspirationApi(authorizedServerAxios)
  const collectionsApi = new CollectionsApi(authorizedServerAxios)

  const [userProfile, recentGuidesResult, recentCollectionsResult] =
    await Promise.all([
      userApi.fetchUserProfile(),
      inspirationApi
        .fetchUserInspirations({
          status: InspirationPublishedStatus,
          page: 1,
          perPage: 1,
        })
        .catch(() => ({
          data: [],
          meta: emptyMeta,
        })),
      collectionsApi
        .fetchCollections({
          isPublic: true,
          page: 1,
          perPage: 1,
        })
        .catch(() => ({
          data: [],
          meta: emptyMeta,
        })),
    ])

  const recentGuideImage = recentGuidesResult.data[0]?.photos?.[0]?.url
  const recentCollectionImage = recentCollectionsResult.data[0]?.images?.[0]
  const guidesCount = recentGuidesResult.meta?.total_count ?? 0
  const collectionsCount = recentCollectionsResult.meta?.total_count ?? 0

  return {
    userProfile,
    recentGuideImage,
    recentCollectionImage,
    guidesCount,
    collectionsCount,
  }
}
